import { COHORT_TIER_KEY } from '~~/constants/discounts';
import { POINTS_HISTORY_ENTRY_TYPE } from '~~/types/pointsHistory/pointsHistory';
import type { MockedPartnersData } from '~~/types/partnersMock/partnersMock';
import type { PointsHistoryEntry } from '~~/types/pointsHistory/pointsHistory';

type MockedInvoice = Pick<PointsHistoryEntry, 'date' | 'points'> & {
  orderNumber: string;
  amount: number;
  currency: string;
};

const invoiceEntry = ({ orderNumber, date, points, amount, currency }: MockedInvoice): PointsHistoryEntry => ({
  type: POINTS_HISTORY_ENTRY_TYPE.INVOICE,
  orderNumber,
  invoiceNumber: `FV/${date.slice(0, 4)}/${orderNumber}`,
  date,
  points,
  amount,
  currency,
});

const retentionEntry = (date: string, points: number): PointsHistoryEntry => ({
  type: POINTS_HISTORY_ENTRY_TYPE.RETENTION,
  orderNumber: null,
  invoiceNumber: null,
  date,
  points,
  amount: null,
  currency: null,
});

// Newest first, as the Customer Data Platform returns it; adds up to the active preset's 1240 points,
// with a return, an order invoiced in two parts and the migration's level-retention row.
const ACTIVE_POINTS_HISTORY: PointsHistoryEntry[] = [
  invoiceEntry({ orderNumber: '100567', date: '2026-10-02', points: 333, amount: 1332, currency: 'PLN' }),
  invoiceEntry({ orderNumber: '100234', date: '2026-09-20', points: -30, amount: -120, currency: 'PLN' }),
  invoiceEntry({ orderNumber: '100234', date: '2026-09-14', points: 120, amount: 480, currency: 'PLN' }),
  invoiceEntry({ orderNumber: '100198', date: '2026-08-03', points: 200, amount: 800, currency: 'PLN' }),
  invoiceEntry({ orderNumber: '100151', date: '2026-06-21', points: 150, amount: 600, currency: 'PLN' }),
  invoiceEntry({ orderNumber: '100151', date: '2026-06-18', points: 100, amount: 400, currency: 'PLN' }),
  retentionEntry('2026-01-01', 367),
];

export const PARTNERS_MOCK_QUERY_PARAMETER = 'partnersMock';
export const PARTNERS_MOCK_COOKIE = 'partnersMock';
export const PARTNERS_MOCK_OFF = 'off';

export const PARTNERS_MOCK_DATA: Record<string, MockedPartnersData> = {
  'new': {
    customerGroupKey: COHORT_TIER_KEY.TIER_0,
    loyaltyPoints: {
      points: 0,
      pointsToNextLevel: 1,
      amountToNextLevel: 4,
      currency: 'PLN',
      monetaryCohortGroup: COHORT_TIER_KEY.TIER_0,
      spendings: [],
    },
    pointsHistory: [],
  },
  'active': {
    customerGroupKey: COHORT_TIER_KEY.TIER_1,
    loyaltyPoints: {
      points: 1240,
      pointsToNextLevel: 760,
      amountToNextLevel: 3040,
      currency: 'PLN',
      monetaryCohortGroup: COHORT_TIER_KEY.TIER_1,
      spendings: [{ currency: 'PLN', amount: 5332 }],
    },
    pointsHistory: ACTIVE_POINTS_HISTORY,
  },
  'multi-currency': {
    customerGroupKey: COHORT_TIER_KEY.TIER_1,
    loyaltyPoints: {
      points: 1240,
      pointsToNextLevel: 760,
      amountToNextLevel: 3040,
      currency: 'PLN',
      monetaryCohortGroup: COHORT_TIER_KEY.TIER_1,
      spendings: [
        { currency: 'PLN', amount: 4000 },
        { currency: 'EUR', amount: 300 },
      ],
    },
    pointsHistory: [
      invoiceEntry({ orderNumber: '100612', date: '2026-10-05', points: 75, amount: 300, currency: 'EUR' }),
      invoiceEntry({ orderNumber: '100567', date: '2026-10-02', points: 333, amount: 1332, currency: 'PLN' }),
      invoiceEntry({ orderNumber: '100198', date: '2026-08-03', points: 465, amount: 1860, currency: 'PLN' }),
      retentionEntry('2026-01-01', 367),
    ],
  },
  'top': {
    customerGroupKey: COHORT_TIER_KEY.TIER_4,
    loyaltyPoints: {
      points: 12500,
      pointsToNextLevel: null,
      amountToNextLevel: null,
      currency: 'PLN',
      monetaryCohortGroup: COHORT_TIER_KEY.TIER_4,
      spendings: [{ currency: 'PLN', amount: 50000 }],
    },
    pointsHistory: [
      invoiceEntry({ orderNumber: '100890', date: '2026-10-12', points: 2500, amount: 10000, currency: 'PLN' }),
      invoiceEntry({ orderNumber: '100702', date: '2026-08-29', points: 5000, amount: 20000, currency: 'PLN' }),
      retentionEntry('2026-01-01', 5000),
    ],
  },
  'cdp-error': {
    customerGroupKey: null,
    loyaltyPoints: null,
    pointsHistory: null,
  },
};
